import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Alert,
  Box, 
  Button, 
  Paper, 
  Snackbar,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  TableSortLabel,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Collapse,
  Tooltip,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { menuApi, type MenuItem } from '../../api/menuApi';

// Create a new interface that matches MenuItem but with our custom properties
interface FlattenedMenuItem {
  // Required properties from MenuItem
  id: string;
  title: string;
  path: string;
  icon: string | null;
  order: number;
  isGroup: boolean;
  isActive: boolean;
  sortOrder: number;
  isDeleted: boolean;
  isSystem: boolean;
  deletedAt: string | null;
  parentId: string | null;
  
  // Our custom properties
  level: number;
  isExpanded: boolean;
  hasChildren: boolean;
  children?: FlattenedMenuItem[];
}

const MenuList: React.FC = () => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState<FlattenedMenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<FlattenedMenuItem | null>(null);
  const [menuToDelete, setMenuToDelete] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    autoHideDuration?: number;
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 10,
  });

  // Fetch all menus
  const fetchMenus = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching menus...');
      const menus = await menuApi.getAllFlat();
      console.log('Fetched menus:', menus);
      
      // Ensure we have valid data
      if (!menus || !Array.isArray(menus)) {
        throw new Error('Invalid response format');
      }
      
      // Log the first few items for debugging
      console.log('Fetched menu items:', menus.slice(0, 3));
      
      // Process the response to ensure all required fields are present and convert to proper types
      const processedMenus = menus.map(menu => {
        // Create a new object that matches FlattenedMenuItem
        const flattened: FlattenedMenuItem = {
          id: menu.id,
          title: menu.title,
          path: menu.path,
          icon: menu.icon,
          order: menu.order,
          isGroup: menu.isGroup,
          isActive: Boolean(menu.isActive),
          sortOrder: menu.sortOrder || 0,
          isDeleted: Boolean(menu.isDeleted),
          isSystem: Boolean(menu.isSystem),
          deletedAt: menu.deletedAt,
          parentId: menu.parentId,
          level: 0,
          isExpanded: false,
          hasChildren: false
        };
        return flattened;
      });
      
      console.log('Processed menus with proper types:', processedMenus);
      setMenus(processedMenus);
    } catch (err) {
      console.error('Error fetching menus:', err);
      setError('Failed to load menus. Please try again later.');
      setMenus([]); // Reset menus on error
    } finally {
      setLoading(false);
    }
  }, []);

  // Flatten menu tree for display
  const flattenMenuTree = (items: MenuItem[], level = 0, parentId: string | null = null): FlattenedMenuItem[] => {
    return items.reduce<FlattenedMenuItem[]>((acc, item) => {
      // Ensure hasChildren is always a boolean
      const hasChildren = !!(item.children && item.children.length > 0);
      
      // Create a new object with all required properties
      const flattenedItem: FlattenedMenuItem = {
        // Map all properties from MenuItem to FlattenedMenuItem
        id: item.id || '',
        title: item.title || '',
        path: item.path || '',
        icon: item.icon || null,
        order: item.order || 0,
        isGroup: item.isGroup || false,
        isActive: item.isActive !== undefined ? item.isActive : true,
        sortOrder: item.sortOrder || 0,
        isDeleted: item.isDeleted || false,
        isSystem: item.isSystem || false,
        deletedAt: item.deletedAt || null,
        parentId: item.parentId !== undefined ? item.parentId : parentId,
        // Add our custom properties
        level,
        hasChildren,
        isExpanded: expandedMenus[item.id] || false,
        // Initialize children as undefined, we'll handle it separately
        children: undefined
      };
      
      // Process children if they exist
      if (hasChildren && item.children) {
        flattenedItem.children = flattenMenuTree(item.children, level + 1, item.id);
      }
      
      acc.push(flattenedItem);
      return acc;
    }, []);
  };

  // Toggle expanded state for a menu item
  const toggleMenuExpand = (menuId: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Handle view menu
  const handleViewMenu = (menu: FlattenedMenuItem) => {
    setSelectedMenu(menu);
    setViewDialogOpen(true);
  };

  // Handle close view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedMenu(null);
  };

  // Handle delete menu
  const handleDelete = async () => {
    if (!menuToDelete) return;
    
    setLoading(true);
    
    try {
      // Use the correct API method for deleting a menu
      await menuApi.menus.delete(menuToDelete);
      // Refresh the menu list
      await fetchMenus();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Menu deleted successfully',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error deleting menu:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete menu',
        severity: 'error',
      });
    } finally {
      setLoading(false);
      setMenuToDelete(null);
      setDeleteDialogOpen(false);
    }
  };
  
  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (menuId: string) => {
    setMenuToDelete(menuId);
    setDeleteDialogOpen(true);
  };

  // Handle close delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMenuToDelete(null);
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter menus based on search term
  const filteredMenus = menus.filter(menu =>
    menu.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (menu.path && menu.path.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      page: 0,
      rowsPerPage: parseInt(event.target.value, 10)
    }));
  };

  // Fetch menus on component mount
  useEffect(() => {
    fetchMenus();
  }, [fetchMenus]);

  // View Dialog component
  const ViewMenuDialog = () => {
    if (!selectedMenu) return null;
    
    return (
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Menu Details</span>
            <IconButton onClick={handleCloseViewDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Title</Typography>
              <Typography variant="body1">{selectedMenu.title}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Path</Typography>
              <Typography variant="body1">{selectedMenu.path || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Status</Typography>
              <Chip
                label={selectedMenu.isActive ? 'Active' : 'Inactive'}
                color={selectedMenu.isActive ? 'success' : 'default'}
                size="small"
                variant={selectedMenu.isActive ? 'filled' : 'outlined'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Type</Typography>
              <Typography variant="body1">
                {selectedMenu.isGroup ? 'Menu Group' : 'Menu Item'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Icon</Typography>
              <Typography variant="body1">{selectedMenu.icon || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Order</Typography>
              <Typography variant="body1">{selectedMenu.order}</Typography>
            </Grid>
            {selectedMenu.parentId && (
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="textSecondary">Parent Menu</Typography>
                <Typography variant="body1">
                  {menus.find(m => m.id === selectedMenu.parentId)?.title || selectedMenu.parentId}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">System Menu</Typography>
              <Box display="flex" alignItems="center">
                {selectedMenu.isSystem ? (
                  <CheckIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                ) : (
                  <CloseIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                )}
                <Typography variant="body2">
                  {selectedMenu.isSystem ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="textSecondary">Deleted</Typography>
              <Box display="flex" alignItems="center">
                {selectedMenu.isDeleted ? (
                  <CheckIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                ) : (
                  <CloseIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                )}
                <Typography variant="body2">
                  {selectedMenu.isDeleted ? 'Yes' : 'No'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseViewDialog} color="primary">
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleCloseViewDialog();
              navigate(`/settings/menus/edit/${selectedMenu.id}`);
            }}
            disabled={selectedMenu.isSystem || selectedMenu.isDeleted}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Delete Confirmation Dialog
  const DeleteConfirmationDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={() => setDeleteDialogOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="warning" />
        Confirm Delete
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to delete this menu? This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={() => setDeleteDialogOpen(false)}
          variant="outlined"
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleDelete} 
          color="error" 
          variant="contained"
          startIcon={<CheckIcon />}
          autoFocus
          disabled={loading}
        >
          {loading ? 'Deleting...' : 'Confirm Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Menu Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/settings/menus/new')}
        >
          Add Menu
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search menus..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Path</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMenus.length > 0 ? (
                filteredMenus.map((menu) => (
                  <TableRow key={menu.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" pl={menu.level * 2}>
                        {menu.hasChildren && (
                          <IconButton
                            size="small"
                            onClick={() => toggleMenuExpand(menu.id)}
                            sx={{ mr: 1 }}
                          >
                            {expandedMenus[menu.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        )}
                        <span>{menu.title}</span>
                      </Box>
                    </TableCell>
                    <TableCell>{menu.path || '-'}</TableCell>
                    <TableCell>
                      <Tooltip title={menu.isActive ? 'Click to deactivate' : 'Click to activate'}>
                        <Chip
                          label={menu.isActive ? 'Active' : 'Inactive'}
                          color={menu.isActive ? 'success' : 'default'}
                          size="small"
                          variant={menu.isActive ? 'filled' : 'outlined'}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              // Optimistically update the UI
                              setMenus(prevMenus => 
                                prevMenus.map(m => 
                                  m.id === menu.id 
                                    ? { ...m, isActive: !menu.isActive } 
                                    : m
                                )
                              );
                              
                              // Make the API call with a timeout
                              const timeoutPromise = new Promise((_, reject) => 
                                setTimeout(() => reject(new Error('Request timed out. The server is taking too long to respond.')), 10000)
                              );
                              
                              // Race between the API call and the timeout
                              await Promise.race([
                                menuApi.menus.update(menu.id, { isActive: !menu.isActive }),
                                timeoutPromise
                              ]);
                              
                              // Refresh the menu list after successful update
                              await fetchMenus();
                              
                              setSnackbar({
                                open: true,
                                message: `Menu ${!menu.isActive ? 'activated' : 'deactivated'} successfully`,
                                severity: 'success',
                              });
                            } catch (err) {
                              console.error('Error updating menu status:', err);
                              
                              // Revert the optimistic update
                              setMenus(prevMenus => 
                                prevMenus.map(m => 
                                  m.id === menu.id 
                                    ? { ...m, isActive: menu.isActive } 
                                    : m
                                )
                              );
                              
                              setSnackbar({
                                open: true,
                                message: `Failed to update menu status: ${err instanceof Error ? err.message : 'Unknown error'}`,
                                severity: 'error',
                                autoHideDuration: 10000, // Show error for longer
                              });
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.9,
                              transform: 'scale(1.03)',
                              transition: 'all 0.2s ease-in-out',
                            },
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      {menu.isGroup ? 'Group' : 'Item'}
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={1}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewMenu(menu)}
                            color="primary"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/settings/menus/edit/${menu.id}`)}
                            disabled={menu.isSystem || menu.isDeleted}
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDeleteDialog(menu.id)}
                            disabled={menu.isSystem || menu.isDeleted}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No menus found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredMenus.length}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Dialogs */}
      <ViewMenuDialog />
      <DeleteConfirmationDialog />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration || 6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuList;
